import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { List, History, Send, Clock, CheckCircle, AlertCircle, Trash2, ChevronLeft, ChevronRight, Share2, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { StatCard } from '@/components/shared/StatCard'
import {
  useFbPosts,
  useDeleteFbPost,
  useClearDonePosts,
  useClearFailedPosts,
  useFbLogs,
  useFbLogStats,
  useClearFbLogs,
  useUnroutableJobs,
} from '@/api/hooks/useExtension'
import { ProfileFilter, statusBadge } from './shared'

export function FilaTab() {
  const { t } = useLanguage()
  const [subTab, setSubTab] = useState<'ativa' | 'historico'>('ativa')

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={subTab === 'ativa' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs" onClick={() => setSubTab('ativa')}>
          <List className="h-3.5 w-3.5" />{t('Fila Ativa')}
        </Button>
        <Button variant={subTab === 'historico' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs border-border/40" onClick={() => setSubTab('historico')}>
          <History className="h-3.5 w-3.5" />{t('Historico')}
        </Button>
      </div>

      {subTab === 'ativa' ? <QueueSection /> : <HistorySection />}
    </div>
  )
}

function QueueSection() {
  const { t } = useLanguage()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [profileFilter, setProfileFilter] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading } = useFbPosts(statusFilter, page, profileFilter || undefined)
  const { data: unroutable } = useUnroutableJobs()
  const deletePost = useDeleteFbPost()
  const clearDone = useClearDonePosts()
  const clearFailed = useClearFailedPosts()

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />

  return (
    <div className="space-y-4">
      {unroutable && unroutable.total > 0 && (
        <div className="mb-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-200">
          <strong>⚠ {unroutable.total} jobs</strong> aguardando perfil com acesso à página necessária.
          {unroutable.online_profiles === 0
            ? ' Nenhum perfil online.'
            : ` ${unroutable.online_profiles} perfil(is) online sem acesso.`}
        </div>
      )}

      <ProfileFilter value={profileFilter} onChange={(v) => { setProfileFilter(v); setPage(1) }} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1.5">
          {[undefined, 'pending', 'processing', 'done', 'failed'].map(s => (
            <Badge key={s ?? 'all'} variant={statusFilter === s ? 'default' : 'outline'} className="cursor-pointer rounded-lg border-border/40 transition-all duration-200" onClick={() => { setStatusFilter(s); setPage(1) }}>
              {s ? s : t('Todos')}
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-lg border-border/40 text-xs" onClick={() => clearDone.mutate()}>{t('Limpar concluidos')}</Button>
          <Button size="sm" variant="outline" className="rounded-lg border-border/40 text-xs" onClick={() => clearFailed.mutate()}>{t('Limpar falhos')}</Button>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground tabular-nums">{data?.total ?? 0} {t('itens na fila')}</div>

      <div className="overflow-hidden rounded-2xl border border-border/40 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent bg-muted/20">
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Grupo')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Perfil')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Pagina')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Mensagem')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Status')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Criado')}</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted/30 mx-auto mb-2">
                    <List className="h-5 w-5 text-muted-foreground/40" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('Fila vazia')}</p>
                </TableCell>
              </TableRow>
            )}
            {data?.data.map(p => (
              <TableRow key={p.id} className="transition-colors hover:bg-muted/20">
                <TableCell className="max-w-[180px] truncate text-xs">{p.fbGroupName}</TableCell>
                <TableCell>
                  {p.profileName ? <Badge variant="secondary" className="text-[10px] rounded-md">{p.profileName}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
                <TableCell>
                  {p.fbPageName ? <Badge variant="outline" className="text-[10px] rounded-md border-border/40">{p.fbPageName}</Badge> : <span className="text-muted-foreground text-[10px]">{t('Pessoal')}</span>}
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                  {p.shareUrl ? (
                    <span className="flex items-center gap-1"><Share2 className="h-3 w-3 text-primary shrink-0" />{p.message ? p.message.substring(0, 60) : t('Compartilhamento')}</span>
                  ) : p.message.substring(0, 80)}
                </TableCell>
                <TableCell>{statusBadge(p.status)}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground tabular-nums">{new Date(p.createdAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell>
                  {(p.status === 'pending' || p.status === 'failed') && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive transition-colors" onClick={() => deletePost.mutate(p.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data && data.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[10px] text-muted-foreground tabular-nums px-1">{page} / {data.last_page}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

function HistorySection() {
  const { t } = useLanguage()
  const [page, setPage] = useState(1)
  const [profileFilter, setProfileFilter] = useState('')
  const { data: logs, isLoading } = useFbLogs(page, undefined, profileFilter || undefined)
  const { data: stats } = useFbLogStats()
  const clearLogs = useClearFbLogs()

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Send} label={t('Total Posts')} value={String(stats.total)} />
          <StatCard icon={CheckCircle} label={t('Sucesso')} value={`${stats.successRate}%`} />
          <StatCard icon={AlertCircle} label={t('Falhas')} value={String(stats.failed)} />
          <StatCard icon={Clock} label={t('Hoje')} value={String(stats.today)} />
        </div>
      )}

      <ProfileFilter value={profileFilter} onChange={(v) => { setProfileFilter(v); setPage(1) }} />

      <div className="flex justify-between items-center">
        <div className="text-[10px] text-muted-foreground tabular-nums">{logs?.total ?? 0} {t('registros')}</div>
        <Button size="sm" variant="outline" className="rounded-lg border-border/40 gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => { if (confirm(t('Limpar todo o historico?'))) clearLogs.mutate() }}>
          <Trash2 className="h-3 w-3" />{t('Limpar')}
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border/40 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent bg-muted/20">
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Grupo')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Pagina')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Mensagem')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Status')}</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Data')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted/30 mx-auto mb-2">
                    <History className="h-5 w-5 text-muted-foreground/40" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('Nenhum registro')}</p>
                </TableCell>
              </TableRow>
            )}
            {logs?.data.map(l => (
              <TableRow key={l.id} className="transition-colors hover:bg-muted/20">
                <TableCell className="max-w-[200px] truncate text-xs">{l.fbGroupName}</TableCell>
                <TableCell>
                  {l.fbPageName ? <Badge variant="outline" className="text-[10px] rounded-md border-border/40">{l.fbPageName}</Badge> : <span className="text-muted-foreground text-[10px]">{t('Pessoal')}</span>}
                </TableCell>
                <TableCell className="max-w-[250px] truncate text-xs text-muted-foreground">{l.message}</TableCell>
                <TableCell>
                  <Badge variant={l.status === 'success' ? 'secondary' : 'destructive'} className="rounded-md text-[10px]">
                    {l.status === 'success' ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                    {l.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-[10px] text-muted-foreground tabular-nums">{new Date(l.postedAt).toLocaleString('pt-BR')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {logs && logs.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[10px] text-muted-foreground tabular-nums px-1">{page} / {logs.last_page}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={page >= logs.last_page} onClick={() => setPage(page + 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}
